// Login modal: provider redirects (GitHub/Google), Telegram note, XHR passkey login.
// Watching never requires login — this opens only on comment/account actions.
import { useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Chrome, Fingerprint, Github } from "lucide-react";
import { api } from "../api/client.js";
import { setToken } from "../auth/token.js";
import { env } from "../env.js";
import { useLoginModal } from "../ui/loginModal.js";
import { Button } from "./ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog.js";

async function passkeyLogin(): Promise<void> {
  const startRes = await fetch(api.url("/auth/passkey/login/start"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!startRes.ok) throw new Error("passkey-start-failed");
  const options = await startRes.json();
  const flowId = options.flowId;

  const assertion = await startAuthentication({ optionsJSON: options });

  const finishRes = await fetch(api.url("/auth/passkey/login/finish"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response: assertion, flowId }),
  });
  if (!finishRes.ok) throw new Error("passkey-finish-failed");
  const j = await finishRes.json();
  if (j?.token) setToken(j.token); // cookie-less: store the session token
}

function providerUrl(provider: string): string {
  const next = encodeURIComponent(location.href);
  return api.url(`/auth/${provider}/start?next=${next}`);
}

export function LoginModal() {
  const { t } = useTranslation();
  const { open, closeLogin } = useLoginModal();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirect = (provider: string) => {
    window.location.href = providerUrl(provider);
  };

  const onPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      await passkeyLogin();
      await qc.invalidateQueries({ queryKey: ["me"] });
      closeLogin();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      setError(msg === "passkey-start-failed" ? t("login.errStart") : t("login.errFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeLogin()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("login.title", { site: env.siteName })}</DialogTitle>
          <DialogDescription>{t("login.subtitle")}</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive-soft">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          <Button variant="secondary" disabled={busy} onClick={() => redirect("github")}>
            <Github className="h-4 w-4" />
            {t("login.github")}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => redirect("google")}>
            <Chrome className="h-4 w-4" />
            {t("login.google")}
          </Button>
          <Button variant="outline" disabled={busy} onClick={onPasskey}>
            <Fingerprint className="h-4 w-4" />
            {busy ? t("login.passkeyWaiting") : t("login.passkey")}
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">{t("login.telegram")}</p>
      </DialogContent>
    </Dialog>
  );
}
